import { storeCreator } from '@/utils/store/store.utils';

import {
  AccountFundingConfigPaylod,
  AUTO_FUNDING_PREFIX,
  CATEGORY_SPEND_LIMIT_PREFIX,
  CategorySpendLimitPayload,
  CoverageLevelPayload,
  SPEND_LIMIT_PREFIX,
} from '../CoverageLevels.constants';

type CoverageLevelsArray = CoverageLevelPayload[];

export interface CoverageLevelsStoreState {
  state: CoverageLevelsArray;
  sourceState: CoverageLevelsArray;
  showErrors: boolean;

  setState: (values: CoverageLevelsArray) => void;
  setSourceState: (values: CoverageLevelsArray) => void;
  setShowErrors: (value: boolean) => void;
  setErrors: (values: CoverageLevelsArray) => void;
  handleReset: () => void;
}

export const useStore = storeCreator<CoverageLevelsStoreState>((set) => ({
  state: [],
  sourceState: [],
  showErrors: false,

  handleReset: () =>
    set(() => ({
      state: [],
      sourceState: [],
      showErrors: false,
    })),
  setShowErrors: (value) => set(() => ({ showErrors: value })),
  setErrors: () => set(() => ({})),
  setState: (values) => set(() => ({ state: values })),
  setSourceState: (values) => set(() => ({ sourceState: values })),
}));

/**
 * Parses a flat form key into a config field update.
 * Returns null if the key is not a config field.
 */
const parseConfigKey = (key: string): { planId: string; field: keyof AccountFundingConfigPaylod } | null => {
  if (key.startsWith(AUTO_FUNDING_PREFIX)) {
    return { planId: key.replace(AUTO_FUNDING_PREFIX, ''), field: 'autoEnrollmentAmount' };
  }
  if (key.startsWith(SPEND_LIMIT_PREFIX)) {
    return { planId: key.replace(SPEND_LIMIT_PREFIX, ''), field: 'perPersonSpendLimit' };
  }
  return null;
};

/**
 * Parses a flat form key into a category spend limit update.
 * Key format: categorySpendLimit_{planId}_{categoryType}
 * Returns null if the key is not a category spend limit field.
 */
const parseCategorySpendLimitKey = (key: string): { planId: string; categoryType: string } | null => {
  if (key.startsWith(CATEGORY_SPEND_LIMIT_PREFIX)) {
    const rest = key.replace(CATEGORY_SPEND_LIMIT_PREFIX, '');
    const separatorIndex = rest.indexOf('_');
    if (separatorIndex === -1) return null;
    const planId = rest.substring(0, separatorIndex);
    const categoryType = rest.substring(separatorIndex + 1);
    return { planId, categoryType };
  }
  return null;
};

export type FormUpdates = Partial<CoverageLevelPayload> & Record<string, unknown>;

/**
 * Updates a single coverage level item by coverageId.
 *
 * FlexControlledForm returns flat keys like:
 *   autoFundingAmount_{planId} → accountFundingConfigs[].autoEnrollmentAmount
 *   perPersonSpendLimit_{planId} → accountFundingConfigs[].perPersonSpendLimit
 *   coverageCode, planDeductible, etc. → top-level fields
 */
export const updateItemByCoverageId = (coverageId: string, updates: FormUpdates) => {
  const { state, setState } = useStore.getState();

  setState(
    state.map((item) => {
      if (item?.coverageId !== coverageId) return item;

      const configs = item.accountFundingConfigs;
      const fieldUpdates: Partial<CoverageLevelPayload> = {};
      const configPatches: Record<string, Partial<AccountFundingConfigPaylod>> = {};
      const categoryPatches: Record<string, Partial<CategorySpendLimitPayload>> = {};

      for (const [key, value] of Object.entries(updates)) {
        const parsed = parseConfigKey(key);
        if (parsed) {
          configPatches[parsed.planId] = {
            ...configPatches[parsed.planId],
            [parsed.field]: String(value ?? ''),
          };
          continue;
        }

        const categoryParsed = parseCategorySpendLimitKey(key);
        if (categoryParsed) {
          const patchKey = `${categoryParsed.planId}_${categoryParsed.categoryType}`;
          categoryPatches[patchKey] = {
            planId: categoryParsed.planId,
            categoryType: categoryParsed.categoryType,
            categorySpendLimit: String(value ?? ''),
          };
          continue;
        }

        fieldUpdates[key as keyof CoverageLevelPayload] = value as never;
      }

      const updatedConfigs = configs?.length
        ? configs.map((config) =>
            configPatches[config.planId] ? { ...config, ...configPatches[config.planId] } : config
          )
        : configs;

      const updatedCategoryLimits = Object.keys(categoryPatches).length
        ? (item.categorySpendLimits || []).map((limit) => {
            const patchKey = `${limit.planId}_${limit.categoryType}`;
            return categoryPatches[patchKey] ? { ...limit, ...categoryPatches[patchKey] } : limit;
          })
        : item.categorySpendLimits;

      return {
        ...item,
        ...fieldUpdates,
        accountFundingConfigs: updatedConfigs,
        categorySpendLimits: updatedCategoryLimits,
      };
    })
  );
};

export default useStore;
